import { useEffect, useState } from 'react';
import { api, formatMoney } from '@/lib/api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Users, Package, ShoppingBag, DollarSign, TrendingUp } from 'lucide-react';

const Kpi = ({ icon: Icon, label, value, hint, testId }) => (
    <div className="rounded-xl border bg-card p-4" data-testid={testId}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="h-3.5 w-3.5" />{label}</div>
        <div className="mt-1 text-2xl font-semibold tabular-nums" data-testid={`${testId}-value`}>{value}</div>
        {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
);

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get('/admin/stats').then(r => setStats(r.data)); }, []);
    if (!stats) return <div className="text-sm text-muted-foreground">Cargando métricas…</div>;

    return (
        <div className="space-y-6 admin-halo">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
                <p className="text-sm text-muted-foreground">Vista general de la plataforma.</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi icon={Users} label="Usuarios activos" value={stats.users.active} hint={`${stats.users.total} totales`} testId="kpi-users" />
                <Kpi icon={Package} label="Anuncios publicados" value={stats.ads.published} hint={`${stats.ads.total} totales`} testId="kpi-ads" />
                <Kpi icon={ShoppingBag} label="Órdenes pagadas" value={stats.orders.paid} hint={`${stats.orders.released} liberadas`} testId="kpi-orders" />
                <Kpi icon={DollarSign} label="Comisiones" value={formatMoney(stats.commissions)} hint={`GMV: ${formatMoney(stats.gmv)}`} testId="kpi-commissions" />
            </div>

            <div className="rounded-xl border bg-card p-4">
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <h2 className="font-medium">Ventas de los últimos 14 días</h2>
                </div>
                <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={stats.series}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="date" fontSize={10} tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                            <YAxis fontSize={10} tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                            <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                            <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="rounded-xl border bg-card p-4">
                <h2 className="font-medium mb-3">Órdenes recientes</h2>
                {stats.recent_orders.length === 0 ? <div className="text-sm text-muted-foreground">Aún no hay órdenes.</div> :
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-muted-foreground text-left"><tr><th className="py-2 pr-2">Fecha</th><th>Producto</th><th>Total</th><th>Estado</th></tr></thead>
                        <tbody>
                            {stats.recent_orders.map(o => (
                                <tr key={o.id} className="border-t">
                                    <td className="py-2 pr-2 text-xs">{new Date(o.created_at).toLocaleString('es-MX')}</td>
                                    <td className="py-2 pr-2 truncate max-w-[240px]">{o.ad_snapshot?.title}</td>
                                    <td className="py-2 pr-2 tabular-nums">{formatMoney(o.total, o.currency?.toUpperCase())}</td>
                                    <td className="py-2 pr-2 capitalize">{o.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>}
            </div>
        </div>
    );
}
